/**
 * EffectsTab — opacity, transform, transition, interaction, layout, and advanced controls.
 * Single responsibility: effects and interaction style properties.
 */

import React, { useState, useEffect } from 'react';
import type { StyleProperties } from '../types';
import { SliderInput } from '../shared/SliderInput';
import { NumberInput } from '../shared/NumberInput';
import { SegmentedControl } from '../shared/SegmentedControl';
import { PresetChips } from '../shared/PresetChips';
import { SectionLabel } from '../shared/SectionLabel';

export interface EffectsTabProps {
  value: StyleProperties;
  onChange: (p: StyleProperties) => void;
  isDark: boolean;
}

// ── Transform string parsing ────────────────────────────────────────────────

interface ParsedTransform {
  scale: number;
  rotate: number;
  translateX: number;
  translateY: number;
}

function parseTransformString(t: string | undefined): ParsedTransform {
  const defaults: ParsedTransform = { scale: 1, rotate: 0, translateX: 0, translateY: 0 };
  if (!t) return defaults;
  try {
    const scale = parseTransformFn(t, 'scale') ?? 1;
    const rotate = parseTransformFn(t, 'rotate') ?? 0;
    const txm = t.match(/translateX\(([^)]+)\)/);
    const tym = t.match(/translateY\(([^)]+)\)/);
    const translateX = txm ? parseFloat(txm[1]) : 0;
    const translateY = tym ? parseFloat(tym[1]) : 0;
    return { scale, rotate, translateX, translateY };
  } catch {
    return defaults;
  }
}

function parseTransformFn(t: string, fn: string): number | undefined {
  const re = new RegExp(`${fn}\\(([^)]+)\\)`);
  const m = t.match(re);
  if (!m) return undefined;
  const raw = m[1].replace('deg', '').replace('px', '');
  const n = parseFloat(raw);
  return isNaN(n) ? undefined : n;
}

function composeTransform(scale: number, rotate: number, tx: number, ty: number): string | undefined {
  const parts: string[] = [];
  if (scale !== 1) parts.push(`scale(${scale})`);
  if (rotate !== 0) parts.push(`rotate(${rotate}deg)`);
  if (tx !== 0) parts.push(`translateX(${tx}px)`);
  if (ty !== 0) parts.push(`translateY(${ty}px)`);
  return parts.length > 0 ? parts.join(' ') : undefined;
}

// ── Transition string parsing ───────────────────────────────────────────────

interface ParsedTransition {
  duration: number;
  easing: string;
}

function parseTransitionString(t: string | undefined): ParsedTransition {
  if (!t) return { duration: 300, easing: 'ease' };
  try {
    // "all 300ms ease" → duration=300, easing='ease'
    const m = t.match(/all\s+(\d+)ms\s+(\S+)/);
    if (m) return { duration: parseInt(m[1], 10), easing: m[2] };
    return { duration: 300, easing: 'ease' };
  } catch {
    return { duration: 300, easing: 'ease' };
  }
}

// ── Constants ───────────────────────────────────────────────────────────────

const EASING_OPTIONS = [
  { value: 'ease', label: 'Ease' },
  { value: 'ease-in', label: 'In' },
  { value: 'ease-out', label: 'Out' },
  { value: 'ease-in-out', label: 'In-Out' },
  { value: 'linear', label: 'Linear' },
] as const;

const CURSOR_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'pointer', label: 'Pointer' },
  { value: 'not-allowed', label: 'N/A' },
  { value: 'grab', label: 'Grab' },
  { value: 'text', label: 'Text' },
  { value: 'crosshair', label: 'Cross' },
] as const;

const USER_SELECT_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'none', label: 'None' },
] as const;

const POINTER_EVENTS_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'none', label: 'None' },
] as const;

const DISPLAY_OPTIONS = [
  { value: 'block', label: 'Block' },
  { value: 'flex', label: 'Flex' },
  { value: 'grid', label: 'Grid' },
  { value: 'inline-block', label: 'Inline' },
] as const;

const FLEX_DIR_OPTIONS = [
  { value: 'row', label: 'Row' },
  { value: 'column', label: 'Col' },
  { value: 'row-reverse', label: 'Row↩' },
  { value: 'column-reverse', label: 'Col↩' },
] as const;

const JUSTIFY_OPTIONS = [
  { value: 'flex-start', label: 'Start' },
  { value: 'flex-end', label: 'End' },
  { value: 'center', label: 'Center' },
  { value: 'space-between', label: 'Between' },
  { value: 'space-around', label: 'Around' },
  { value: 'space-evenly', label: 'Evenly' },
] as const;

const ALIGN_ITEMS_OPTIONS = [
  { value: 'flex-start', label: 'Start' },
  { value: 'flex-end', label: 'End' },
  { value: 'center', label: 'Center' },
  { value: 'stretch', label: 'Stretch' },
  { value: 'baseline', label: 'Baseline' },
] as const;

const FLEX_WRAP_OPTIONS = [
  { value: 'nowrap', label: 'No-wrap' },
  { value: 'wrap', label: 'Wrap' },
  { value: 'wrap-reverse', label: 'Wrap↩' },
] as const;

const CLIP_PATH_PRESETS = [
  { label: 'None', value: '' },
  { label: 'Circle', value: 'circle(50%)', title: 'circle(50%)' },
  { label: 'Rounded', value: 'inset(0 round 12px)', title: 'inset(0 round 12px)' },
  { label: 'Diagonal', value: 'polygon(0 0, 100% 0, 100% 85%, 0 100%)', title: 'Diagonal clip' },
];

/**
 * EffectsTab — opacity, transform, transition, interaction, layout, and advanced.
 */
export const EffectsTab: React.FC<EffectsTabProps> = ({ value, onChange, isDark }) => {
  // Transform decomposition (local state for composing)
  const parsedT = parseTransformString(value.transform);
  const [scale, setScale] = useState<number>(parsedT.scale);
  const [rotate, setRotate] = useState<number>(parsedT.rotate);
  const [translateX, setTranslateX] = useState<number>(parsedT.translateX);
  const [translateY, setTranslateY] = useState<number>(parsedT.translateY);

  // Transition decomposition
  const parsedTr = parseTransitionString(value.transition);
  const [duration, setDuration] = useState<number>(parsedTr.duration);
  const [easing, setEasing] = useState<string>(parsedTr.easing);

  // Re-sync transform when external value changes
  useEffect(() => {
    const p = parseTransformString(value.transform);
    setScale(p.scale);
    setRotate(p.rotate);
    setTranslateX(p.translateX);
    setTranslateY(p.translateY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.transform]);

  // Re-sync transition when external value changes
  useEffect(() => {
    const p = parseTransitionString(value.transition);
    setDuration(p.duration);
    setEasing(p.easing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.transition]);

  const textPrimary = isDark ? '#e2e8f0' : '#1e293b';
  const inputBg = isDark ? 'rgba(2,6,23,0.6)' : '#f8fafc';
  const borderColor = isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0';

  const stackStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  };

  const clipInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '5px 8px',
    borderRadius: 4,
    border: `1px solid ${borderColor}`,
    background: inputBg,
    color: textPrimary,
    fontSize: 12,
    boxSizing: 'border-box',
    fontFamily: 'monospace',
    marginTop: 4,
  };

  const applyTransform = (s: number, r: number, tx: number, ty: number) => {
    onChange({ ...value, transform: composeTransform(s, r, tx, ty) });
  };

  const applyTransition = (d: number, e: string) => {
    onChange({ ...value, transition: `all ${d}ms ${e}` });
  };

  const isFlex = value.display === 'flex';

  // Opacity: stored as 0-1, displayed as 0-100
  const opacityPct = value.opacity !== undefined ? Math.round(value.opacity * 100) : undefined;

  return (
    <div style={stackStyle}>
      <SectionLabel label="Visibility" isDark={isDark} />
      <SliderInput
        label="Opacity"
        value={opacityPct}
        onChange={(v) => onChange({ ...value, opacity: v !== undefined ? v / 100 : undefined })}
        min={0}
        max={100}
        step={1}
        unit="%"
        isDark={isDark}
      />

      <SectionLabel label="Transform" isDark={isDark} />
      <SliderInput
        label="Scale"
        value={scale}
        onChange={(v) => {
          const s = v ?? 1;
          setScale(s);
          applyTransform(s, rotate, translateX, translateY);
        }}
        min={0.1}
        max={3.0}
        step={0.05}
        isDark={isDark}
      />
      <SliderInput
        label="Rotate"
        value={rotate}
        onChange={(v) => {
          const r = v ?? 0;
          setRotate(r);
          applyTransform(scale, r, translateX, translateY);
        }}
        min={-180}
        max={180}
        step={1}
        unit="°"
        isDark={isDark}
      />
      <NumberInput
        label="Translate X"
        value={translateX}
        onChange={(v) => {
          const tx = v ?? 0;
          setTranslateX(tx);
          applyTransform(scale, rotate, tx, translateY);
        }}
        unit="px"
        isDark={isDark}
      />
      <NumberInput
        label="Translate Y"
        value={translateY}
        onChange={(v) => {
          const ty = v ?? 0;
          setTranslateY(ty);
          applyTransform(scale, rotate, translateX, ty);
        }}
        unit="px"
        isDark={isDark}
      />

      <SectionLabel label="Transition" isDark={isDark} />
      <SliderInput
        label="Duration"
        value={duration}
        onChange={(v) => {
          const d = v ?? 300;
          setDuration(d);
          applyTransition(d, easing);
        }}
        min={0}
        max={1000}
        step={50}
        unit="ms"
        isDark={isDark}
      />
      <SegmentedControl
        label="Easing"
        options={EASING_OPTIONS as unknown as Array<{ value: string; label: string }>}
        value={easing}
        onChange={(v) => {
          setEasing(v);
          applyTransition(duration, v);
        }}
        isDark={isDark}
      />

      <SectionLabel label="Interaction" isDark={isDark} />
      <SegmentedControl
        label="Cursor"
        options={CURSOR_OPTIONS as unknown as Array<{ value: string; label: string }>}
        value={value.cursor}
        onChange={(v) => onChange({ ...value, cursor: v })}
        isDark={isDark}
      />
      <SegmentedControl
        label="User Select"
        options={USER_SELECT_OPTIONS as unknown as Array<{ value: string; label: string }>}
        value={value.userSelect}
        onChange={(v) => onChange({ ...value, userSelect: v as StyleProperties['userSelect'] })}
        isDark={isDark}
      />
      <SegmentedControl
        label="Pointer Events"
        options={POINTER_EVENTS_OPTIONS as unknown as Array<{ value: string; label: string }>}
        value={value.pointerEvents}
        onChange={(v) => onChange({ ...value, pointerEvents: v as StyleProperties['pointerEvents'] })}
        isDark={isDark}
      />

      <SectionLabel label="Layout (containers)" isDark={isDark} />
      <SegmentedControl
        label="Display"
        options={DISPLAY_OPTIONS as unknown as Array<{ value: string; label: string }>}
        value={value.display}
        onChange={(v) => onChange({ ...value, display: v as StyleProperties['display'] })}
        isDark={isDark}
      />

      {isFlex && (
        <>
          <SegmentedControl
            label="Direction"
            options={FLEX_DIR_OPTIONS as unknown as Array<{ value: string; label: string }>}
            value={value.flexDirection}
            onChange={(v) => onChange({ ...value, flexDirection: v as StyleProperties['flexDirection'] })}
            isDark={isDark}
          />
          <SegmentedControl
            label="Justify"
            options={JUSTIFY_OPTIONS as unknown as Array<{ value: string; label: string }>}
            value={value.justifyContent}
            onChange={(v) => onChange({ ...value, justifyContent: v as StyleProperties['justifyContent'] })}
            isDark={isDark}
          />
          <SegmentedControl
            label="Align"
            options={ALIGN_ITEMS_OPTIONS as unknown as Array<{ value: string; label: string }>}
            value={value.alignItems}
            onChange={(v) => onChange({ ...value, alignItems: v as StyleProperties['alignItems'] })}
            isDark={isDark}
          />
          <SegmentedControl
            label="Wrap"
            options={FLEX_WRAP_OPTIONS as unknown as Array<{ value: string; label: string }>}
            value={value.flexWrap}
            onChange={(v) => onChange({ ...value, flexWrap: v as StyleProperties['flexWrap'] })}
            isDark={isDark}
          />
        </>
      )}

      <SectionLabel label="Advanced" isDark={isDark} />
      <PresetChips
        label="Clip Path"
        chips={CLIP_PATH_PRESETS}
        value={value.clipPath ?? ''}
        onChange={(v) => onChange({ ...value, clipPath: v || undefined })}
        isDark={isDark}
      />
      <input
        type="text"
        value={value.clipPath ?? ''}
        onChange={(e) => onChange({ ...value, clipPath: e.target.value || undefined })}
        placeholder="e.g. circle(50%)"
        style={clipInputStyle}
        aria-label="Clip path custom value"
        spellCheck={false}
      />
      <NumberInput
        label="Z-Index"
        value={value.zIndex}
        onChange={(v) => onChange({ ...value, zIndex: v })}
        isDark={isDark}
      />
    </div>
  );
};

EffectsTab.displayName = 'EffectsTab';
